import Router from 'next/router';
import { equals } from 'ramda';
import { ofType } from 'redux-observable';
import { concat, defer, interval, Observable, of } from 'rxjs';
import { catchError, mergeMap, take, withLatestFrom } from 'rxjs/operators';
import { Weddings } from '@bridebook/models';
import { ISupplier } from '@bridebook/models/source/models/Suppliers.types';
import { ValidationError } from '@bridebook/toolbox/src';
import { IUISupplier } from '@bridebook/toolbox/src/types';
import { TWithSupplierData } from '@bridebook/web/app-shared/lib/supplier/supplier-types';
import { appError } from '@bridebook/web/lib/app/actions';
import getAuthStatus from '@bridebook/web/lib/auth/utils/get-auth-status';
import { toggleSnackbar } from '@bridebook/web/lib/bbcommon/actions';
import { EnquiriesActionTypes } from '@bridebook/web/lib/enquiries/action-types';
import {
  createEnquiryFormEditSchema,
  type EnquiryFormValues,
} from '@bridebook/web/lib/enquiries/schemas/enquiry-form-schema';
import { getI18n } from '@bridebook/web/lib/i18n/getI18n';
import { getIsUK } from '@bridebook/web/lib/i18n/selectors';
import { saveToShortlist } from '@bridebook/web/lib/shortlist/actions';
import { getBookedVenuesList } from '@bridebook/web/lib/shortlist/selectors';
import { TrackingEvent } from '@bridebook/web/lib/track-utils/tracking-event';
import type { EnquiryDetailType } from '@bridebook/web/lib/enquiries/LIVE-27666-enquiry-form-analytics.types';
import {
  Action,
  EnquiryFormFieldsType,
  EnquiryFormInfoProps,
  IApplicationState,
  IDeps,
  IEpicDeps,
  IProfile,
  IUserSerialized,
  ShortlistedLocation,
} from '@bridebook/web/lib/types';
import { UserActionTypes } from '@bridebook/web/lib/users/action-types';
import { updateUserEmail, updateUserPhone } from '@bridebook/web/lib/users/actions';
import { toUrlQuery } from '@bridebook/web/lib/utils';
import { getAsPathNormalized } from '@bridebook/web/lib/utils/url';
import { updateWeddingField } from '@bridebook/web/lib/weddings/actions';
import { WeddingActionTypes } from '../weddings/action-types';
import {
  confirmedEnquiryDetailsAnalytics,
  failedToConfirmDetailsAnalytics,
  failedToSendEnquiryAnalytics,
} from './analytics/actions';
import SendEnquiryError from './errors/send-enquiry-error';
import { IEnquiryDate, IEnquiryFormToggleArgs } from './types';

import { EnquiryIntents } from '@bridebook/web/lib/enquiries/intent';

export const setEnquirySupplier = ({ supplier }: { supplier: IUISupplier }) => ({
  type: EnquiriesActionTypes.SET_ENQUIRY_SUPPLIER,
  payload: supplier,
});

export const resetEnquiryForm =
  () =>
  ({ getState }: IDeps) => {
    const {
      weddings: { profile },
      users: { user },
    } = getState();

    return {
      type: EnquiriesActionTypes.RESET_ENQUIRY_FORM,
      payload: { profile, user },
    };
  };

export const onSupplierEnquiryInteractAnalytics = (
  type: string,
  infoProps: EnquiryFormInfoProps = {},
  message?: string,
  intent?: EnquiryIntents,
  // LIVE-27666 only — the opened detail section; omitted (and not tracked) for control.
  enquiryDetailType?: EnquiryDetailType,
) => ({
  type: EnquiriesActionTypes.ON_SUPPLIER_ENQUIRY_INTERACT_ANALYTICS,
  payload: { type, ...infoProps, message, intent, enquiryDetailType },
});

export const enquiryFormToggle =
  ({
    show = true,
    infoProps = {},
    track = true,
    supplier = null,
    resetEnquiry = true,
    startInEditMode = false,
  }: IEnquiryFormToggleArgs) =>
  (deps: IDeps) => {
    const { getState, dispatch, cordovaTracker } = deps;
    const {
      enquiries: { enquirySupplier, infoProps: currentInfoProps },
    } = getState();
    const trackedInfoProps = show ? infoProps : { ...currentInfoProps, ...infoProps };

    // Resetting enquiry data here causes the analytics sent to use default contact intent data
    // instead of the one provided by the user when closing the form.
    if (resetEnquiry) {
      dispatch(resetEnquiryForm());
    }

    const actualSupplier = enquirySupplier || supplier;
    if (show && actualSupplier?.type === 'venue') {
      cordovaTracker.trackOnce(TrackingEvent.TriggeredEnquiryForm, {
        supplierId: actualSupplier.id,
        supplierType: actualSupplier.type,
        countryCode: actualSupplier.countryCode,
      });
    }
    return {
      type: EnquiriesActionTypes.ENQUIRY_FORM_TOGGLE,
      payload: {
        show,
        supplier: actualSupplier,
        infoProps: trackedInfoProps,
        track,
        startInEditMode,
      },
    };
  };

export const setLoggedOutEnquiryTriggered = () => ({
  type: EnquiriesActionTypes.SET_LOGGED_OUT_ENQUIRY_TRIGGERED,
});

export const sendEnquirySuccess =
  (supplier: IUISupplier, multipleEnquiry?: boolean, noConfirmation?: boolean) =>
  ({ getState, dispatch, queryClient }: IDeps) => {
    const state = getState();
    const {
      shortlist: { list },
      enquiries: { userEnquiryAnalyticsMethod, infoProps, intent },
      users: { user },
      weddings: {
        profile: { id: weddingId },
      },
    } = state;

    const { method: infoPropsMethod, contactLocation, contactSection } = infoProps;

    const { query } = Router;
    const { enquiryConfirmation } = query;

    dispatch(fetchEnquiryDates());

    const isLoggedIn = getAuthStatus({ user: getState().users.user });

    const { id, type } = supplier;
    const isShortlisted = list && Boolean(list[id]);

    if (!isShortlisted) {
      dispatch(
        saveToShortlist({
          supplier: { ...supplier, enquired: true },
          method: 'onEnquirySend',
          shortlistedLocation: (contactLocation || infoPropsMethod) as ShortlistedLocation,
          ...(contactSection && { shortlistedSection: contactSection }),
          showSnackbar: false,
        }),
      );
    } else {
      Weddings._.getById(weddingId).Suppliers.getById(id).update({ enquired: true });
    }

    const isVenue = type === 'venue' || (Array.isArray(type) && type.includes('venue'));
    const isPhotographer = type === 'photo' || (Array.isArray(type) && type.includes('photo'));
    const bookedVenuesList = getBookedVenuesList(state);

    const isUK = getIsUK(state);
    if (
      isUK &&
      isVenue &&
      bookedVenuesList.length < 1 &&
      !enquiryConfirmation &&
      isLoggedIn &&
      !multipleEnquiry &&
      !noConfirmation
    ) {
      dispatch(
        toggleEnquiryConfirmation({
          show: true,
          enquirySupplierId: supplier.publicId,
        }),
      );
    }
    // https://bridebook.atlassian.net/browse/LIVE-15607 enquiry confirmation is only active when book photographer context is active
    else if (
      isPhotographer &&
      isUK &&
      infoProps.method === 'linkedSupplier_weddingConfirmationPopup'
    ) {
      dispatch(
        toggleEnquiryConfirmation({
          show: true,
          enquirySupplierId: supplier.publicId,
        }),
      );
    } else {
      const i18n = getI18n();
      dispatch(enquiryFormToggle({ show: false, track: false }));
      const messageEmail = user && user.contacts ? user.contacts.email : 'your email address';
      const brochureEmail = user && user.contacts ? user.contacts.email : 'you now';

      const brochureSentCopy = i18n.t('enquiries:enquirySent.brochure', { brochureEmail });

      const messageSentCopy = i18n.t('enquiries:enquirySent.message', {
        count: multipleEnquiry ? 2 : 1,
      });

      const repliesCopy = i18n.t('enquiries:enquirySent.repliesInbox', { messageEmail });

      const message =
        userEnquiryAnalyticsMethod === 'supplierProfile_topButtonDownloadable'
          ? brochureSentCopy
          : `${messageSentCopy} ${repliesCopy}`;

      dispatch(toggleSnackbar('success', message));
    }

    queryClient.invalidateQueries(['requested-showrounds', supplier.id]);

    return {
      type: EnquiriesActionTypes.SEND_ENQUIRY_SUCCESS,
      payload: { ...supplier, intent },
    };
  };

export const sendEnquiryError =
  (error: Error, supplier?: IUISupplier, normalizeFailedReason?: boolean) => (deps: IDeps) => {
    const { dispatch } = deps;

    dispatch(
      failedToSendEnquiryAnalytics(error, supplier, {
        normalizeFailedReason,
      }),
    );

    if (error instanceof ValidationError || error instanceof SendEnquiryError) {
      const message = 'messageI18n' in error ? error.messageI18n || error.message : error.message;

      dispatch(toggleSnackbar('alert', message));
    }

    return {
      type: EnquiriesActionTypes.SEND_ENQUIRY_ERROR,
      payload: error,
    };
  };

export const closeEnquiryOnEnquiryLimitEpic = (action$: Observable<any>) =>
  action$.pipe(
    ofType(EnquiriesActionTypes.SHOW_ENQUIRIES_USER_LIMIT_MODAL),
    mergeMap(() =>
      interval(100).pipe(
        take(1),
        mergeMap(() => of(enquiryFormToggle({ show: false }))),
      ),
    ),
  );

export const confirmEnquiryFormError = (error: ValidationError) => (deps: IDeps) => {
  const { dispatch } = deps;

  dispatch(failedToConfirmDetailsAnalytics(error));
  const i18n = getI18n();
  dispatch(toggleSnackbar('alert', i18n.t('enquiries:error.someDetailsMissing')));

  return {
    type: EnquiriesActionTypes.CONFIRM_ENQUIRY_FORM_ERROR,
    payload: error,
  };
};

const enquiriesGetUpdateActions = ({
  profile,
  user,
  fields,
}: {
  profile: IProfile;
  user: IUserSerialized | null;
  fields: Omit<EnquiryFormValues, 'message'> & { message?: string };
}) => {
  const actions = [];

  /* weddingDate */
  if (fields.weddingDate && !equals(profile.date, fields.weddingDate)) {
    actions.push(of(updateWeddingField('date', { date: fields.weddingDate })));
  }

  /* partners */
  if (!equals(profile.partners, fields.partners)) {
    actions.push(of(updateWeddingField('partners', { partners: fields.partners })));
  }
  /* location */
  if (
    !equals(profile.location, fields.location) &&
    fields.location &&
    fields.location.name !== ''
  ) {
    actions.push(of(updateWeddingField('location', { location: fields.location })));
  }
  /* guestsInitialTarget */
  if (profile.guests.estimate !== fields.guestsInitialTarget) {
    actions.push(
      of(
        updateWeddingField('guests', {
          guests: { estimate: fields.guestsInitialTarget },
        }),
      ),
    );
  }

  /* email */
  if (user?.contacts?.email !== fields.email) {
    actions.push(of(updateUserEmail(fields.email)));
  }

  /* phone */
  if (user?.contacts?.phone !== fields.phone) {
    actions.push(of(updateUserPhone(fields.phone)));
  }

  return actions;
};

const updateWeddingWithEnquiryFieldsSuccess =
  () =>
  ({ getState, dispatch }: IDeps) => {
    const state = getState();

    const { enquiryConfirmationScreenShown } = state.enquiries;
    const enabled = false; // here was feature toggle before

    if (enquiryConfirmationScreenShown && enabled) {
      dispatch(confirmedEnquiryDetailsAnalytics('edit'));
    }
    dispatch(toggleEnquiryConfirmationScreen(false));

    return {
      type: EnquiriesActionTypes.UPDATE_WEDDING_WITH_ENQUIRY_FIELDS_SUCCESS,
    };
  };

export const toggleEnquiryConfirmationScreen = (show: boolean) => ({
  type: EnquiriesActionTypes.TOGGLE_ENQUIRY_CONFIRMATION_SCREEN,
  payload: show,
});

export const updateWeddingWithEnquiryFieldsError =
  (error: ValidationError<keyof EnquiryFormFieldsType>) =>
  ({ dispatch }: IDeps) => {
    const i18n = getI18n();
    const name = error.prop;
    const isUserDetails = name === 'email' || name === 'phone';
    const errorMessage = isUserDetails
      ? error.messageI18n || error.message
      : i18n.t('common:error.fillOutMissingFields');

    if (isUserDetails) {
      dispatch({
        type: UserActionTypes.UPDATE_USER_DETAILS_ERROR,
        payload: { name, error: error.message },
      });
    } else {
      dispatch({
        type: WeddingActionTypes.UPDATE_WEDDING_FIELD_ERROR,
        payload: { name, error: error.message },
      });
    }

    dispatch(toggleSnackbar('alert', errorMessage));

    return {
      type: EnquiriesActionTypes.UPDATE_WEDDING_WITH_ENQUIRY_FIELDS_ERROR,
      payload: error,
    };
  };

export const updateWeddingWithEnquiryFieldsEpic = (
  action$: Observable<Action>,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType(EnquiriesActionTypes.UPDATE_WEDDING_WITH_ENQUIRY_FIELDS_START),
    withLatestFrom(state$),
    mergeMap(([action, state]: [Action, IApplicationState]) => {
      const {
        enquiries: { enquiryConfirmationScreenShown, enquirySupplier },
        weddings: { profile },
        users: { user },
      } = state;
      const typedAction = action as ReturnType<typeof updateWeddingWithEnquiryFields>;
      const fields = typedAction.payload?.fields;
      const actions = fields ? enquiriesGetUpdateActions({ profile, user, fields }) : [];

      return defer(() => {
        if (fields) {
          const result = createEnquiryFormEditSchema(enquirySupplier?.type ?? []).safeParse(fields);
          if (!result.success) {
            const issue = result.error.issues[0];
            throw new ValidationError(issue.message, String(issue.path[0] ?? ''));
          }
        }
        typedAction.payload?.onSettle?.();
        return concat(of(updateWeddingWithEnquiryFieldsSuccess()), ...actions);
      }).pipe(
        catchError((error) => {
          if (enquiryConfirmationScreenShown) {
            return of(confirmEnquiryFormError(error));
          }
          return of(
            updateWeddingWithEnquiryFieldsError(error),
            appError({ error, feature: 'Enquiries' }),
          );
        }),
      );
    }),
  );

export const updateWeddingWithEnquiryFields = (payload?: {
  fields?: Omit<EnquiryFormValues, 'message'> & { message?: string };
  onSettle?: () => void;
  onError?: () => void;
}) => ({
  type: EnquiriesActionTypes.UPDATE_WEDDING_WITH_ENQUIRY_FIELDS_START,
  payload,
});

export const fetchEnquiryConfirmationSupplier = (supplierId: string) => ({
  type: EnquiriesActionTypes.FETCH_ENQUIRY_CONFIRMATION_SUPPLIER_START,
  payload: supplierId,
});

export const fetchEnquiryConfirmationSupplierSuccess =
  ({
    enquiryConfirmationSupplier,
    enquiredSuppliersCount,
  }: {
    enquiryConfirmationSupplier: TWithSupplierData<ISupplier>;
    enquiredSuppliersCount: number;
  }) =>
  ({ dispatch }: IDeps) => {
    dispatch(enquiryFormToggle({ show: false, track: false }));

    return {
      type: EnquiriesActionTypes.FETCH_ENQUIRY_CONFIRMATION_SUPPLIER_SUCCESS,
      payload: { enquiryConfirmationSupplier, enquiredSuppliersCount },
    };
  };

export const toggleEnquiryConfirmation = ({
  show,
  enquirySupplierId,
}: {
  show: boolean;
  enquirySupplierId?: string;
}) => {
  const { query } = Router;
  const asPath = getAsPathNormalized();

  // Preserve any existing URL query when toggling enquiry confirmation
  const { enquiryConfirmation, enquirySupplier, ...restQuery } = query;
  // Get path without query params
  const path = asPath.includes('?') ? asPath.slice(0, asPath.indexOf('?')) : asPath;
  // Remove parts not needed for URL
  delete restQuery.searchParams;

  const appendQuery = toUrlQuery({
    ...(show && { enquiryConfirmation: 'true' }),
    ...(show && enquirySupplierId && { enquirySupplier: enquirySupplierId }),
    ...restQuery,
  });

  const href = path + appendQuery;

  if (show) {
    // We need to use router.replace to make sure
    // enquiry modal query params don't get added to router history stack
    // so when navigating back we go back to previous page
    Router.replace(href, undefined, { shallow: true });
  } else {
    Router.replace(href, undefined, { shallow: true });
  }

  return {
    type: EnquiriesActionTypes.TOGGLE_ENQUIRY_CONFIRMATION,
    payload: show,
  };
};

export const fetchEnquiryDates = () => ({
  type: EnquiriesActionTypes.FETCH_ENQUIRY_DATES,
});

export const fetchEnquiryDatesSuccess = (payload: IEnquiryDate[]) => ({
  type: EnquiriesActionTypes.FETCH_ENQUIRY_DATES_SUCCESS,
  payload,
});

export const showEnquiriesUserLimitModal = (show: boolean) => ({
  type: EnquiriesActionTypes.SHOW_ENQUIRIES_USER_LIMIT_MODAL,
  payload: { show },
});

export const toggleMultipleEnquiry = (payload: boolean) => ({
  type: EnquiriesActionTypes.TOGGLE_MULTIPLE_ENQUIRY,
  payload,
});

export const openVenueConfirmationPopup = () => ({
  type: EnquiriesActionTypes.OPEN_CONFIRMATION_MODAL,
});

export const setInfoProps = (infoProps: EnquiryFormInfoProps) => ({
  type: EnquiriesActionTypes.SET_INFO_PROPS,
  payload: infoProps,
});
